<template>
  <FileInput
    id="file-upload"
    ref="fileInput"
    hidden
    :accept="imageTypes"
    :multiple="false"
    :limit="1"
    @input="onInput"
  />
  <PassportTitleBar
    :variant="getVariant(item.type?.value)"
    :title="
      $t('components.passport.images.profile.title', {
        type: $t(
          'components.spaces.types.' + getVariant(item.type?.value) + '.label'
        ),
      })
    "
    @back="$emit('back')"
  >
  </PassportTitleBar>
  <div class="flex flex-col pt-4 pl-10 pr-10 gap-3">
    <div class="overflow-hidden bg-primary rounded-5xl shadow-xl">
      <Indicator
        :variant="getVariant(item.type?.value)"
        mode="transparent"
        :imageUrl="item.imageUrl"
        class="w-full h-full aspect-square"
        :class="{
          '!bg-primary': item.imageUrl,
        }"
      />
    </div>
  </div>
  <div class="flex flex-col justify-center mt-10">
    <div
      class="flex flex-row gap-y-2 text-center justify-center pl-5 pr-5 mb-5"
    >
      <div class="mr-5">
        <Button
          size="lg"
          :label="$t('components.buttons.reset')"
          class="pl-5 pr-5 pt-2 pb-2 rounded-md button-secondary-gray-fg"
          variant="secondary"
          type="gray"
          @click.stop="askResetConfirmation(item.id)"
        />
      </div>
      <div>
        <Button
          size="lg"
          :label="$t('components.buttons.replace')"
          class="pl-5 pr-5 pt-2 pb-2 rounded-md button-secondary-gray-fg"
          variant="secondary"
          type="gray"
          @click="fileInput.click()"
        />
      </div>
    </div>
  </div>
  <DeleteModal
    :open="!!photoBeingReset"
    :title="$t('components.passport.images.profile.reset_title')"
    :description="$t('components.passport.images.profile.reset_description')"
    :confirmLabel="$t('components.buttons.reset')"
    @cancel="photoBeingReset = null"
    @delete="handleResetConfirmed"
  ></DeleteModal>
</template>

<script setup>
import axios from "axios";
import route from "ziggy-js";
import { ref, onMounted } from "vue";
import { PassportTitleBar } from "@/SharedComponents/Passport";
import { Button } from "@/SharedComponents/Buttons";
import { Indicator } from "@/SharedComponents/Indicator";
import { FileInput } from "@/SharedComponents/Inputs";
import { toast } from "@/SharedComponents/Toaster";
import { useI18n } from "vue-i18n";
import { DeleteModal } from "@/SharedComponents/Modals";
import { imageTypes } from "@/SharedComponents/Inputs";

const { t } = useI18n();
const fileInput = ref();
const emit = defineEmits(["back", "update:item"]);
const photoBeingReset = ref(null);

const props = defineProps({
  item: { type: Object, required: true },
  readonly: { type: Boolean, required: false },
  variant: { type: Boolean, required: false },
});

const getVariant = (data) => {
  switch (data) {
    case "asset_group":
    case "asset":
      return data;
    default:
      return "space";
  }
};

const onInput = (files) => {
  let filteredFiles = fileInput.value.filterValidFiles(files);
  uploadFiles(filteredFiles);
};

const askResetConfirmation = (id) => {
  photoBeingReset.value = id;
};

function handleResetConfirmed() {
  axios
    .delete(route("api.spaces.photo.reset", photoBeingReset.value))
    .then(() => {
      toast.success(t("components.passport.images.profile.reset_success"));
      emit("update:item", {
        item: {
          ...props.item,
          imageUrl: null,
        },
      });
      photoBeingReset.value = null;
    })
    .catch(() => {
      toast.error(t("toasts.error"));
    });
}

const uploadFiles = (files) => {
  if (!files || files.length === 0) {
    return;
  }

  files.forEach((file) => {
    let spaceId = props.item.id;
    const formData = new FormData();
    formData.append("photo", file);
    formData.append("space_id", spaceId);

    try {
      axios
        .post(route("api.spaces.photo.replace", { space: spaceId }), formData)
        .then(() => {
          toast.success(
            t("components.passport.images.profile.replace_success")
          );
          fetchObject(spaceId);
        })
        .catch(() => {
          toast.error(t("toasts.error"));
        });
    } catch (error) {
      toast.error(t("toasts.error"));
    }
  });
};

async function fetchObject(spaceId) {
  const response = await axios.get(route("api.spaces.photo.get", spaceId));
  emit("update:item", {
    item: {
      ...props.item,
      imageUrl: response.data.imageUrl,
      thumbnailUrl: response.data.thumbnailUrl,
    },
  });
}

onMounted(() => {
  if (!props.readonly && !props.item.imageUrl) {
    fileInput.value.click();
  }
});
</script>
